import React from 'react';
import { MapPin, Navigation, Clock, Star } from 'lucide-react';

const pointsOfInterest = [
  { name: 'Schoodic Point', distance: '5 miles', time: '10 min', description: 'Dramatic granite shoreline with crashing waves — the quieter side of Acadia', rating: '4.9' },
  { name: 'Acadia National Park (Main)', distance: '15 miles', time: '30 min', description: 'Iconic Cadillac Mountain, Jordan Pond House, and miles of carriage roads', rating: '4.9' },
  { name: 'Winter Harbor', distance: '3 miles', time: '5 min', description: 'Charming village with galleries, provisions, and the famous lobster festival', rating: '4.7' },
  { name: 'Bar Harbor', distance: '25 miles', time: '45 min', description: 'Bustling harbor town with whale watching, shopping, and fine dining', rating: '4.8' },
  { name: 'Schoodic Beach', distance: '4 miles', time: '8 min', description: 'Secluded sandy beach perfect for families and beachcombing', rating: '4.8' },
  { name: 'Tidal Falls Preserve', distance: '12 miles', time: '20 min', description: 'Watch reversing tidal falls and spot seals — great picnic spot', rating: '4.7' },
];

const MapSection = () => {
  return (
    <section id="map" className="section-spacing" data-testid="map-section">
      <div className="container-coastal">
        <div className="text-center mb-xl">
          <h2 className="section-title">Getting Around</h2>
          <p className="section-subtitle" style={{ maxWidth: '650px', margin: '0 auto' }}>
            Explore from Gouldsboro — gateway to Schoodic Peninsula and Acadia National Park
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-xl)', alignItems: 'start' }}>
          {/* Map Embed */}
          <div style={{ borderRadius: 'var(--border-radius-lg)', overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', height: '500px' }}>
            <iframe
              data-testid="google-map-iframe"
              title="Gouldsboro Area Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d90000!2d-68.1!3d44.47!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cae09c99f269275%3A0x78d5e50814d5dc8a!2sGouldsboro%2C%20ME!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Points of Interest */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--color-dark-slate)', marginBottom: '0.5rem' }}>
              Nearby Attractions
            </h3>
            {pointsOfInterest.map((poi, i) => (
              <div key={i} data-testid={`poi-card-${i}`} style={{
                display: 'flex',
                gap: 'var(--spacing-md)',
                padding: 'var(--spacing-md)',
                background: 'white',
                borderRadius: 'var(--border-radius-lg)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
              >
                <div style={{
                  width: '44px', height: '44px', minWidth: '44px',
                  background: 'var(--color-ocean-deep)', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <MapPin size={20} color="white" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <h4 style={{ fontWeight: '700', color: 'var(--color-dark-slate)', fontSize: '1rem' }}>{poi.name}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: 'var(--color-sunset-gold)' }}>
                      <Star size={14} fill="var(--color-sunset-gold)" />
                      {poi.rating}
                    </div>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: 'var(--color-granite-gray)', lineHeight: 1.5, marginBottom: '0.5rem' }}>
                    {poi.description}
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: 'var(--color-harbor-mist)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Navigation size={12} /> {poi.distance}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Clock size={12} /> {poi.time} drive</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 900px) {
          #map .container-coastal > div:last-child {
            grid-template-columns: 1fr !important;
          }
          #map iframe {
            height: 350px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default MapSection;
